import {
  as400BranchSplitLocationsForDivision,
  as400DivisionCode,
  as400DivisionExportsPerBranch,
} from '@erp/rpc';
import type { DivisionOption } from './useDivisionSelection';
import { newRowLocationLabel } from './newRowTargets';

/**
 * The review page's Location filter: the locations of the division whose
 * upload runs per branch (RD, GEN-2720), picked like divisions are. Each is
 * filtered by its whole CDB key (`RDC`), so picking a location of RD never
 * reads as the same letter of another division.
 */

/** One location the filter offers. */
export interface ReviewLocationOption {
  /** The CDB key the query matches: division code, then location (`RDC`). */
  key: string;
  /** The location code, as the button shows it (`C`). */
  code: string;
  /** The code with its DC's name (`C · RODC Ontario`), for the tooltip. */
  label: string;
}

/**
 * The per-branch division the filter is for, with its locations; null when
 * the org has none.
 */
export function reviewLocationDivision(
  dataSources: readonly DivisionOption[],
): { id: string; title: string; options: ReviewLocationOption[] } | null {
  const division = dataSources.find((dataSource) =>
    as400DivisionExportsPerBranch(dataSource.title),
  );
  if (!division) return null;
  const code = as400DivisionCode(division.title);
  return {
    id: division.id,
    title: division.title,
    options: as400BranchSplitLocationsForDivision(division.title).map(
      (location) => ({
        key: `${code}${location}`,
        code: location,
        label: newRowLocationLabel(division.title, location),
      }),
    ),
  };
}

/**
 * The locations the query is narrowed to: the picked keys still on offer, or
 * none while the division they belong to isn't selected — the filter is off
 * then, rather than hiding every row of the divisions that are. A bare code
 * (`C`), saved when the filter was a typed box, reads as that location's key.
 */
export function activeReviewLocations(
  picked: readonly string[],
  options: readonly ReviewLocationOption[],
  enabled: boolean,
): string[] {
  if (!enabled) return [];
  const wanted = new Set(picked);
  const keys: string[] = [];
  for (const option of options) {
    if (wanted.has(option.key) || wanted.has(option.code))
      keys.push(option.key);
  }
  return keys;
}

/** Pick or unpick one location, keeping the rest. */
export function toggleReviewLocation(
  picked: readonly string[],
  key: string,
): string[] {
  return picked.includes(key)
    ? picked.filter((other) => other !== key)
    : [...picked, key];
}
